import React, { useEffect } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { GS_COMPONENTS } from '../tokens';

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'warning' | 'info';
  isLoading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Modal de confirmation générique.
 * Desktop : modal centré. Mobile : bottom sheet.
 *
 * Les classes .animate-dialog / .animate-dialog-backdrop sont fournies par
 * tailwind.config.shared.js (plugin « @gs/ui — composants partagés », keyframes
 * sourcées du thème). Ne PAS les redéfinir localement dans le index.css d'une app.
 */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirmer',
  cancelLabel = 'Annuler',
  variant = 'danger',
  isLoading = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [open, onCancel]);

  if (!open) return null;

  const confirmCls =
    variant === 'danger' ? GS_COMPONENTS.btnDanger : GS_COMPONENTS.btnPrimary;
  const iconColor =
    variant === 'danger'
      ? 'text-red-500'
      : variant === 'warning'
        ? 'text-amber-500'
        : 'text-blue-500';
  const iconBg =
    variant === 'danger'
      ? 'bg-red-50'
      : variant === 'warning'
        ? 'bg-amber-50'
        : 'bg-blue-50';

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      onClick={onCancel}
    >
      <div className="fixed inset-0 bg-black/40 dark:bg-black/60 animate-dialog-backdrop" />
      <div
        className="relative w-full sm:max-w-md mx-0 sm:mx-4
                   bg-white dark:bg-slate-900
                   rounded-t-2xl sm:rounded-2xl
                   border-0 sm:border border-slate-100 dark:border-slate-700
                   shadow-xl p-6 animate-dialog"
        onClick={(e) => e.stopPropagation()}
      >
        {variant !== 'info' && (
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${iconBg}`}>
            <AlertTriangle size={20} className={iconColor} />
          </div>
        )}
        <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100 mb-2">
          {title}
        </h3>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">{message}</p>
        <div className="flex gap-3 justify-end">
          <button type="button" onClick={onCancel} className={GS_COMPONENTS.btnSecondary}>
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isLoading}
            className={`${confirmCls} disabled:opacity-50 inline-flex items-center gap-2`}
          >
            {isLoading && <Loader2 size={14} className="animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
